import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { i18n, t } = useTranslation()

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/about', label: t('nav.about') },
    { path: '/faq', label: t('nav.faq') },
    { path: '/contact', label: t('nav.contact') }
  ]

  const isActive = (path: string) => location.pathname === path

  // Close mobile menu when clicking outside
  const closeMobileMenu = () => setMobileMenuOpen(false)

  // Language toggle
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en'
    i18n.changeLanguage(newLang)
  }

  return (
    <nav className="bg-white shadow-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" onClick={closeMobileMenu}>
            <img
              src="https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/Gemini_Generated_Image_zga35ozga35ozga3.png"
              alt="Masala Chai Tea Logo"
              className="h-14 md:h-20 w-auto"
              crossOrigin="anonymous"
            />
            <span className="text-2xl md:text-3xl text-[#E2725B]" style={{ fontFamily: 'Great Vibes, cursive', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
              Masala Chai Tea
            </span>
          </Link>

          {/* Desktop Navigation - hidden on mobile */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-terracotta-600 border-b-2 border-terracotta-600'
                    : 'text-gray-700 hover:text-terracotta-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Toggle - Desktop */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-4 py-2 bg-[#E2725B] hover:bg-[#d65f48] text-white rounded-lg transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              aria-label="Toggle language"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span className="text-sm font-bold">{i18n.language === 'en' ? 'ES' : 'EN'}</span>
            </button>
          </div>

          {/* Mobile Menu Button - visible on mobile, hidden on desktop */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Mobile Menu Panel */}
          <div className="fixed right-0 top-0 h-full w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <img
                    src="https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/Gemini_Generated_Image_zga35ozga35ozga3.png"
                    alt="Masala Chai Tea Logo"
                    className="h-12 w-auto"
                    crossOrigin="anonymous"
                  />
                  <span className="text-xl text-[#E2725B]" style={{ fontFamily: 'Great Vibes, cursive', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
                    Masala Chai Tea
                  </span>
                </div>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close mobile menu"
                >
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Navigation Links */}
              <nav className="flex flex-col p-6 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={closeMobileMenu}
                    className={`px-4 py-3 rounded-lg font-medium text-lg transition-colors duration-200 ${
                      isActive(link.path)
                        ? 'bg-terracotta-100 text-terracotta-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {/* Language Toggle - Mobile */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <button
                    onClick={toggleLanguage}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#E2725B] hover:bg-[#d65f48] text-white rounded-lg transition-all duration-200 font-bold shadow-md hover:shadow-lg"
                    aria-label="Toggle language"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span className="text-lg">
                      {i18n.language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
                    </span>
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
